import { useEffect, useRef, ReactNode, useState } from 'react'
import { TelegramContext } from '@/shared/lib/telegram'
import { createMockTelegramWebApp } from '@/shared/lib/telegram/mock-telegram'
import { env } from '@/shared/config/env'
import { ENABLE_MOCK_AUTH, MOCK_INIT_DATA } from '@/shared/config/dev-mocks'
import { initializeTheme, getStoredTheme, applyTheme } from '@/shared/lib/theme'
import bridge from '@vkontakte/vk-bridge'
import {
  createVkWebAppAdapter,
  subscribeToButtons,
  subscribeToVkTheme,
  mainButtonState,
  secondaryButtonState
} from '@/shared/lib/telegram/vk-bridge-adapter'

interface TelegramProviderProps {
  children: ReactNode
}

export function TelegramProvider({ children }: TelegramProviderProps) {
  const [, setCurrentTheme] = useState<'light' | 'dark'>('light')
  const [isInitialized, setIsInitialized] = useState(false)
  const [platformState, setPlatformState] = useState<{
    webApp: any
    initData: string
    isVk: boolean
    vkUserInfo: any
  }>({
    webApp: null,
    initData: '',
    isVk: false,
    vkUserInfo: null,
  })

  // State to force re-render when virtual buttons change
  const [buttonTick, setButtonTick] = useState(0)
  const buttonBarRef = useRef<HTMLDivElement | null>(null)

  // Subscribe to virtual button state changes — runs for ALL platforms
  useEffect(() => {
    return subscribeToButtons(() => {
      setButtonTick(t => t + 1)
    })
  }, [])

  // Measure the actual rendered height of the button bar and update CSS variable
  useEffect(() => {
    const el = buttonBarRef.current
    const height = el ? Math.ceil(el.getBoundingClientRect().height) : 0
    document.documentElement.style.setProperty('--bottom-bar-height', `${height}px`)
  }, [buttonTick])

  useEffect(() => {
    async function initPlatform() {
      // 1. Detect VK
      const isVk = typeof window !== 'undefined' && window.location.search.includes('vk_app_id')

      if (isVk) {
        console.log('[Platform] Detected VK Mini App environment')
        try {
          // Initialize VK Bridge
          await bridge.send('VKWebAppInit')
          
          // Request notifications permission (non-blocking)
          bridge.send('VKWebAppAllowNotifications').catch(err => {
            console.warn('[Platform] VK notifications permission request failed/denied:', err)
          })
          
          // Get user info
          const vkUserInfo = await bridge.send('VKWebAppGetUserInfo')
          console.log('[Platform] VK user info retrieved:', vkUserInfo)

          const vkInitData = window.location.search.slice(1) // Keep the query parameters as initData
          const adapter = await createVkWebAppAdapter(vkInitData, vkUserInfo)
          
          // Set window.Telegram.WebApp for compatibility
          ;(window as any).Telegram = {
            WebApp: adapter,
          }

          // Initialize theme
          const colorScheme = adapter.colorScheme as 'light' | 'dark'
          const effectiveTheme = initializeTheme(colorScheme, (theme) => {
            setCurrentTheme(theme)
          })
          setCurrentTheme(effectiveTheme)

          setPlatformState({
            webApp: adapter,
            initData: vkInitData,
            isVk: true,
            vkUserInfo,
          })

          // Listen to VK theme changes (VKWebAppUpdateConfig event).
          // useTheme() subscribes directly too — here we just keep currentTheme state in sync.
          const unsubscribeTheme = subscribeToVkTheme((theme) => {
            const storedTheme = getStoredTheme()
            if (!storedTheme || storedTheme === 'platform') {
              applyTheme(theme)
              setCurrentTheme(theme)
            }
          })

          // Redirect if launched from VK notification deep-link (fragment parameter is passed as hash)
          if (typeof window !== 'undefined' && window.location.hash) {
            const hashPath = window.location.hash.slice(1)
            if (hashPath.startsWith('order/') || hashPath.startsWith('catalog')) {
              window.history.replaceState(null, '', '/' + hashPath)
            }
          }

          setIsInitialized(true)
          return () => { unsubscribeTheme() }
        } catch (err) {
          console.error('[Platform] VK initialization failed:', err)
        }
      }

      // 2. Mock Auth Mode
      if (ENABLE_MOCK_AUTH) {
        console.warn('⚠️ Running in MOCK AUTH mode. Using mock user data.')
        const mockWebApp = createMockTelegramWebApp()
        mockWebApp.initData = MOCK_INIT_DATA
        ;(window as any).Telegram = {
          WebApp: mockWebApp,
        }
        
        const effectiveTheme = initializeTheme(mockWebApp.colorScheme as 'light' | 'dark')
        applyTheme(effectiveTheme)
        setCurrentTheme(effectiveTheme)

        setPlatformState({
          webApp: mockWebApp,
          initData: MOCK_INIT_DATA,
          isVk: false,
          vkUserInfo: null,
        })
        setIsInitialized(true)
        return
      }
      
      // 3. Telegram Web App
      const isTelegram = typeof window !== 'undefined' && (window as any).Telegram?.WebApp
      const useMock = env.isDev && env.useMockTelegram && !isTelegram
      
      if (useMock) {
        console.warn('⚠️ Running in mock mode. Telegram SDK not available.')
        const mockWebApp = createMockTelegramWebApp()
        ;(window as any).Telegram = {
          WebApp: mockWebApp,
        }
      }

      if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
        const webApp = (window as any).Telegram.WebApp
        
        webApp.expand()
        webApp.enableClosingConfirmation()
        
        const telegramTheme = webApp.colorScheme as 'light' | 'dark'
        const effectiveTheme = initializeTheme(telegramTheme, (theme) => {
          setCurrentTheme(theme)
        })
        setCurrentTheme(effectiveTheme)
        
        webApp.onEvent('themeChanged', () => {
          const storedTheme = getStoredTheme()
          if (!storedTheme || storedTheme === 'platform') {
            const newTheme = webApp.colorScheme as 'light' | 'dark'
            applyTheme(newTheme)
            setCurrentTheme(newTheme)
          }
        })
        
        if (webApp.themeParams.bg_color) {
          document.documentElement.style.setProperty('--tg-theme-bg-color', webApp.themeParams.bg_color)
        }
        if (webApp.themeParams.text_color) {
          document.documentElement.style.setProperty('--tg-theme-text-color', webApp.themeParams.text_color)
        }
        if (webApp.themeParams.button_color) {
          document.documentElement.style.setProperty('--tg-theme-button-color', webApp.themeParams.button_color)
        }
        if (webApp.themeParams.button_text_color) {
          document.documentElement.style.setProperty('--tg-theme-button-text-color', webApp.themeParams.button_text_color)
        }
        
        const bottomBarColor = effectiveTheme === 'dark' ? '#0D0F11' : '#FAF8F5'
        webApp.setBottomBarColor?.(bottomBarColor)
        
        const updateSafeAreaInsets = () => {
          const safeArea = webApp.safeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 }
          const contentSafeArea = webApp.contentSafeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 }
          
          document.documentElement.style.setProperty('--tg-safe-area-inset-top', `${safeArea.top}px`)
          document.documentElement.style.setProperty('--tg-safe-area-inset-bottom', `${safeArea.bottom}px`)
          document.documentElement.style.setProperty('--tg-safe-area-inset-left', `${safeArea.left}px`)
          document.documentElement.style.setProperty('--tg-safe-area-inset-right', `${safeArea.right}px`)
          document.documentElement.style.setProperty('--tg-content-safe-area-inset-top', `${contentSafeArea.top}px`)
          document.documentElement.style.setProperty('--tg-content-safe-area-inset-bottom', `${contentSafeArea.bottom}px`)
        }
        
        updateSafeAreaInsets()
        webApp.onEvent?.('safeAreaChanged', updateSafeAreaInsets)
        webApp.onEvent?.('contentSafeAreaChanged', updateSafeAreaInsets)

        setPlatformState({
          webApp,
          initData: webApp.initData || '',
          isVk: false,
          vkUserInfo: null,
        })
      }
      setIsInitialized(true)
    }

    initPlatform()
  }, [])

  if (!isInitialized || !platformState.webApp) {
    return null // Wait for platform detection/initialization
  }

  return (
    <TelegramContext.Provider value={platformState}>
      {children}
      
      {/* Custom action buttons — rendered for all platforms (VK + Telegram) */}
      {(mainButtonState.isVisible || secondaryButtonState.isVisible) && (
        <div
          ref={buttonBarRef}
          className="fixed left-0 right-0 z-50 flex flex-col gap-2 border-t border-border bg-paper/95 px-4 pt-3 shadow-[0_-12px_32px_rgba(0,0,0,0.12)] backdrop-blur-xl dark:bg-dark-bg/95 dark:shadow-[0_-16px_36px_rgba(0,0,0,0.45)]"
          style={{
            bottom: 'var(--bottom-nav-height, 0px)',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          {secondaryButtonState.isVisible && (
            <button
              onClick={() => secondaryButtonState.onClickCallbacks.forEach(cb => cb())}
              disabled={!secondaryButtonState.isActive || secondaryButtonState.isProgressVisible}
              style={{
                backgroundColor: secondaryButtonState.color || '#f1f1f1',
                color: secondaryButtonState.textColor || '#000000',
              }}
              className="flex min-h-[48px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center font-medium transition-all active:scale-[0.98] active:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {secondaryButtonState.isProgressVisible ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                secondaryButtonState.text
              )}
            </button>
          )}
          {mainButtonState.isVisible && (
            <button
              onClick={() => mainButtonState.onClickCallbacks.forEach(cb => cb())}
              disabled={!mainButtonState.isActive || mainButtonState.isProgressVisible}
              style={{
                backgroundColor: mainButtonState.color || '#2481cc',
                color: mainButtonState.textColor || '#ffffff',
              }}
              className="flex min-h-[52px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center font-semibold transition-all active:scale-[0.98] active:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {mainButtonState.isProgressVisible ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                mainButtonState.text
              )}
            </button>
          )}
        </div>
      )}
    </TelegramContext.Provider>
  )
}
